<script setup>
import { useForm } from '@inertiajs/vue3';
import { getCurrentInstance } from 'vue';
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import Card from 'primevue/card';
import Message from 'primevue/message';
import { useToast } from 'primevue/usetoast';
import ConfirmDialog from 'primevue/confirmdialog';
import { useConfirm } from 'primevue/useconfirm';
import { router } from '@inertiajs/vue3';
import AdminMainLayout from '@/layout/admin/AdminMainLayout.vue';
import { useLoading } from '@/composables/useLoading';

const { proxy } = getCurrentInstance();
const $t = proxy.$t;
const toast = useToast();
const confirm = useConfirm();
const { showLoading, hideLoading } = useLoading();

const props = defineProps({
    language: Object,
});

const form = useForm({
    name: props.language.name,
});

const submit = () => {
    form.put(route('languages.update', props.language.id), {
        onStart: () => showLoading('Updating language...'),
        onFinish: () => hideLoading(),
        onSuccess: () => {
            toast.add({
                severity: 'success',
                summary: 'Success',
                detail: 'Language updated successfully',
                life: 3000
            });
        },
        onError: () => {
            toast.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to update language',
                life: 5000
            });
        }
    });
};

const deleteLanguage = () => {
    confirm.require({
        message: `${$t('Are you sure you want to delete')} "${props.language.name}"? ${$t('This action cannot be undone.')}`,
        header: $t('Delete Confirmation'),
        icon: 'pi pi-exclamation-triangle',
        rejectClass: 'p-button-secondary p-button-outlined',
        acceptClass: 'p-button-danger',
        accept: () => {
            router.delete(route('languages.destroy', props.language.id), {
                onStart: () => showLoading('Deleting language...'),
                onFinish: () => hideLoading(),
                onSuccess: () => {
                    toast.add({
                        severity: 'success',
                        summary: 'Success',
                        detail: 'Language deleted successfully',
                        life: 3000
                    });
                },
                onError: () => {
                    toast.add({
                        severity: 'error',
                        summary: 'Error',
                        detail: 'Failed to delete language',
                        life: 5000
                    });
                }
            });
        }
    });
};
</script>

<template>
    <AdminMainLayout>
        <div class="grid gap-6">
            <ConfirmDialog />

            <div class="col-12 lg:col-8">
                <Card>
                    <template #title>
                        <div class="flex align-items-center gap-2">
                            <i class="pi pi-pencil text-2xl"></i>
                            <span>{{ $t('Edit Language') }}</span>
                        </div>
                    </template>

                    <template #content>
                        <form @submit.prevent="submit">
                            <div class="formgrid grid">
                                <div class="field col-12">
                                    <label for="name" class="font-semibold">{{ $t('Name') }} <span class="text-red-500">*</span></label>
                                    <InputText
                                        id="name"
                                        v-model="form.name"
                                        class="w-full"
                                        :class="{ 'p-invalid': form.errors.name }"
                                        :placeholder="$t('Enter language name')"
                                        autocomplete="name"
                                        :disabled="form.processing" />
                                    <small v-if="form.errors.name" class="p-error">{{ form.errors.name }}</small>
                                </div>
                            </div>

                            <div class="flex gap-2 mt-4">
                                <Button
                                    type="submit"
                                    :label="$t('Save Changes')"
                                    icon="pi pi-check"
                                    :loading="form.processing"
                                    :disabled="form.processing" />

                                <Button
                                    :label="$t('Cancel')"
                                    icon="pi pi-times"
                                    class="p-button-secondary"
                                    @click="() => router.visit(route('languages.index'), {
                                        onStart: () => showLoading('Loading languages...'),
                                        onFinish: () => hideLoading()
                                    })"
                                    :disabled="form.processing" />

                                <Button
                                    :label="$t('Delete')"
                                    icon="pi pi-trash"
                                    class="p-button-danger ml-auto"
                                    @click="deleteLanguage"
                                    :disabled="form.processing" />
                            </div>

                            <Message v-if="form.recentlySuccessful" severity="success" :closable="false" class="mt-3">
                                {{ $t('Language updated successfully.') }}
                            </Message>
                        </form>
                    </template>
                </Card>
            </div>

        <div class="col-12 lg:col-4">
            <Card>
                <template #title>
                    <div class="flex align-items-center gap-2">
                        <i class="pi pi-info-circle"></i>
                        <span>{{ $t('Language Information') }}</span>
                    </div>
                </template>
                <template #content>
                    <div class="field-group">
                        <label class="font-semibold text-gray-600">{{ $t('Language ID:') }}</label>
                        <span class="ml-2">{{ language.id }}</span>
                    </div>

                    <div class="field-group mt-3" v-if="language.sites_count !== undefined">
                        <label class="font-semibold text-gray-600">{{ $t('Associated Sites:') }}</label>
                        <p class="mt-1">
                            <span class="inline-flex align-items-center">
                                <i class="pi pi-globe mr-2"></i>
                                {{ language.sites_count || 0 }} {{ $t('site(s)') }}
                            </span>
                        </p>
                    </div>

                    <div class="field-group mt-3" v-if="language.created_at">
                        <label class="font-semibold text-gray-600">{{ $t('Created') }}:</label>
                        <span class="ml-2">{{ new Date(language.created_at).toLocaleDateString() }}</span>
                    </div>

                    <div class="field-group mt-3" v-if="language.updated_at">
                        <label class="font-semibold text-gray-600">{{ $t('Last Updated') }}:</label>
                        <span class="ml-2">{{ new Date(language.updated_at).toLocaleDateString() }}</span>
                    </div>

                    <div class="mt-4 p-3 bg-yellow-50 border-round">
                        <p class="m-0 text-sm">
                            <i class="pi pi-exclamation-triangle text-yellow-600 mr-2"></i>
                            <strong>{{ $t('Warning:') }}</strong> {{ $t('Deleting this language will remove it from all associated sites.') }}
                        </p>
                    </div>
                </template>
            </Card>
        </div>
        </div>
    </AdminMainLayout>
</template>

<style scoped>
.field {
    margin-bottom: 1.5rem;
}

.field label {
    display: block;
    margin-bottom: 0.5rem;
}

.field-group p {
    margin: 0;
}

.p-error {
    color: var(--red-500);
    font-size: 0.875rem;
    margin-top: 0.25rem;
    display: block;
}

.bg-yellow-50 {
    background-color: #fef9e7;
}

.text-yellow-600 {
    color: #d68910;
}
</style>